import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, TextField, Typography } from '@mui/material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CloseIcon from '@mui/icons-material/Close'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill'
import FormatColorResetIcon from '@mui/icons-material/FormatColorReset'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications'
import Swal from 'sweetalert2'

import {
    deleteMiColorMaquina,
    deleteTipoMaquina,
    getMisColoresMaquinas,
    getSalidasProduccion,
    getTipoMaquina,
    getTiposMaquinas,
    patchMiColorMaquina,
    patchTipoMaquina,
    postTipoMaquina,
} from '../services/apis'
import { LoadingMaestro } from '../components/LoadingMaestro'
import { normalizarArray } from '../utils/apiResponse'
import { APP_DATA } from '../data'

const getApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message

    if (Array.isArray(message)) {
        return message.join('\n')
    }

    return message || fallback
}

const isHexColor = (value) => /^#[0-9A-F]{6}$/i.test(String(value ?? '').trim())

const getCodMaquinaColorUsuario = (colorMaquinaUsuario) => (
    colorMaquinaUsuario?.codMaquina
    ?? colorMaquinaUsuario?.maquina?.codMaquina
    ?? colorMaquinaUsuario?.salidaProduccion?.codMaquina
    ?? ''
)

const getColorMaquinaUsuario = (colorMaquinaUsuario) => (
    colorMaquinaUsuario?.colorMaquina
    ?? colorMaquinaUsuario?.color
    ?? ''
)

export const Maquinas = () => {
    const [tiposMaquinas, setTiposMaquinas] = useState([])
    const [salidasProduccion, setSalidasProduccion] = useState([])
    const [coloresUsuarioMaquinas, setColoresUsuarioMaquinas] = useState([])
    const [guardandoColorMaquina, setGuardandoColorMaquina] = useState(null)
    const [mostrarColoresMaquinas, setMostrarColoresMaquinas] = useState(false)
    const [loading, setLoading] = useState(true)
    const [openTipoMaquina, setOpenTipoMaquina] = useState(false)
    const [tipoMaquinaEditandoCod, setTipoMaquinaEditandoCod] = useState(null)
    const [tipoMaquinaEliminar, setTipoMaquinaEliminar] = useState(null)
    const [tipoMaquinaForm, setTipoMaquinaForm] = useState({
        desTipoMaquina: '',
    })

    const cargarDatosMaquinas = useCallback(async () => {
        setLoading(true)
        try {
            const [tiposResponse, salidasResponse, coloresResponse] = await Promise.all([
                getTiposMaquinas(),
                getSalidasProduccion(),
                getMisColoresMaquinas().catch((error) => {
                    console.error('Error al cargar colores personales de maquinas:', error)
                    return []
                }),
            ])

            setTiposMaquinas(normalizarArray(tiposResponse, 'tiposMaquinas'))
            setSalidasProduccion(normalizarArray(salidasResponse, 'salidasProduccion'))
            setColoresUsuarioMaquinas(normalizarArray(coloresResponse, 'coloresMaquinas'))
        } catch (error) {
            console.error('Error al cargar tipos de maquinas:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar los tipos de maquinas'),
                timer: 2500,
                showConfirmButton: false,
            })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        cargarDatosMaquinas()
    }, [cargarDatosMaquinas])

    const tiposPorCodigo = useMemo(() => (
        tiposMaquinas.reduce((acc, tipoMaquina) => {
            acc[tipoMaquina.codTipoMaquina] = tipoMaquina.desTipoMaquina
            return acc
        }, {})
    ), [tiposMaquinas])

    const coloresPorMaquina = useMemo(() => (
        coloresUsuarioMaquinas.reduce((acc, colorMaquinaUsuario) => {
            const codMaquina = getCodMaquinaColorUsuario(colorMaquinaUsuario)
            const colorMaquina = getColorMaquinaUsuario(colorMaquinaUsuario)

            if (codMaquina && colorMaquina) acc[codMaquina] = colorMaquina

            return acc
        }, {})
    ), [coloresUsuarioMaquinas])

    const resetTipoMaquinaForm = () => {
        setTipoMaquinaForm({
            desTipoMaquina: '',
        })
    }

    const handleChangeTipoMaquina = (event) => {
        const { name, value } = event.target

        setTipoMaquinaForm((tipoMaquinaActual) => ({
            ...tipoMaquinaActual,
            [name]: value,
        }))
    }

    const abrirCrearTipoMaquina = () => {
        resetTipoMaquinaForm()
        setTipoMaquinaEditandoCod(null)
        setOpenTipoMaquina(true)
    }

    const cerrarTipoMaquina = () => {
        setOpenTipoMaquina(false)
        setTipoMaquinaEditandoCod(null)
        resetTipoMaquinaForm()
    }

    const abrirEditarTipoMaquina = async (codTipoMaquina) => {
        try {
            const response = await getTipoMaquina(codTipoMaquina)
            const tipoMaquina = response?.data ?? response

            setTipoMaquinaForm({
                desTipoMaquina: tipoMaquina.desTipoMaquina ?? '',
            })
            setTipoMaquinaEditandoCod(codTipoMaquina)
            setOpenTipoMaquina(true)
        } catch (error) {
            console.error('Error al cargar el tipo de maquina:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar el tipo de maquina'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const guardarTipoMaquina = async (event) => {
        event.preventDefault()

        const tipoMaquinaEnvio = {
            desTipoMaquina: tipoMaquinaForm.desTipoMaquina.trim(),
        }

        try {
            if (tipoMaquinaEditandoCod) {
                await patchTipoMaquina(tipoMaquinaEditandoCod, tipoMaquinaEnvio)
            } else {
                await postTipoMaquina(tipoMaquinaEnvio)
            }

            cerrarTipoMaquina()
            cargarDatosMaquinas()
            Swal.fire({
                icon: 'success',
                title: tipoMaquinaEditandoCod ? 'Tipo de maquina actualizado' : 'Tipo de maquina creado',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al guardar el tipo de maquina:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al guardar el tipo de maquina'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const confirmarEliminarTipoMaquina = async () => {
        if (!tipoMaquinaEliminar) return

        try {
            await deleteTipoMaquina(tipoMaquinaEliminar.codTipoMaquina)
            setTipoMaquinaEliminar(null)
            cargarDatosMaquinas()
            Swal.fire({
                icon: 'success',
                title: 'Tipo de maquina eliminado',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al eliminar el tipo de maquina:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al eliminar el tipo de maquina'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const actualizarColorUsuarioMaquinaLocal = (codMaquina, colorMaquina) => {
        setColoresUsuarioMaquinas((coloresActuales) => {
            const codMaquinaNormalizado = String(codMaquina)
            const coloresSinMaquina = coloresActuales.filter((colorActual) => (
                String(getCodMaquinaColorUsuario(colorActual)) !== codMaquinaNormalizado
            ))

            if (!colorMaquina) return coloresSinMaquina

            return [
                ...coloresSinMaquina,
                {
                    codMaquina,
                    colorMaquina,
                },
            ]
        })
    }

    const guardarColorUsuarioMaquina = async (codMaquina, colorMaquina) => {
        if (!isHexColor(colorMaquina)) return

        setGuardandoColorMaquina(codMaquina)
        actualizarColorUsuarioMaquinaLocal(codMaquina, colorMaquina)

        try {
            const response = await patchMiColorMaquina(codMaquina, colorMaquina)
            const colorGuardado = response?.data ?? response

            actualizarColorUsuarioMaquinaLocal(
                codMaquina,
                getColorMaquinaUsuario(colorGuardado) || colorMaquina
            )
        } catch (error) {
            console.error('Error al guardar el color de maquina:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al guardar el color de maquina'),
                timer: 2500,
                showConfirmButton: false,
            })
            cargarDatosMaquinas()
        } finally {
            setGuardandoColorMaquina(null)
        }
    }

    const eliminarColorUsuarioMaquina = async (codMaquina) => {
        setGuardandoColorMaquina(codMaquina)
        actualizarColorUsuarioMaquinaLocal(codMaquina, '')

        try {
            await deleteMiColorMaquina(codMaquina)
        } catch (error) {
            console.error('Error al quitar el color de maquina:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al quitar el color de maquina'),
                timer: 2500,
                showConfirmButton: false,
            })
            cargarDatosMaquinas()
        } finally {
            setGuardandoColorMaquina(null)
        }
    }

    return (
        <Grid
            container
            direction="row"
            size={{ xs: 12 }}
            sx={{
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Grid
                container
                direction="row"
                size={{ xs: 10 }}
                sx={{
                    justifyContent: "start",
                    alignItems: "start",
                    mt: 3,
                }}
                className='fade-in ajustes-compact'
                spacing={2}
            >
                {loading ? (
                    <LoadingMaestro label="Cargando tipos de maquinas..." />
                ) : (
                <>
                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                    spacing={2}
                >
                    <Grid container direction="column" size={{ xs: 12, md: 7 }} sx={{ alignItems: "start" }}>
                        <Typography variant='h4'>
                            Tipos de maquinas
                        </Typography>

                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                            Maestro de tipos de maquinas disponibles en {APP_DATA.empresaNombre}.
                        </Typography>
                    </Grid>

                    <Grid container size={{ xs: 12, sm: 5, md: 3 }} className='card-main' sx={{ p: 1.5, alignItems: "center" }}>
                        <PrecisionManufacturingIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                            <Typography variant="caption">Tipos de maquinas</Typography>
                            <Typography variant="h5">{tiposMaquinas.length}</Typography>
                        </Box>
                    </Grid>

                    <Grid container direction="row" size={{ xs: 12, sm: 5, md: 2 }} sx={{ justifyContent: { xs: "start", sm: "end" } }}>
                        <Button variant="contained" fullWidth onClick={abrirCrearTipoMaquina}>
                            <AddCircleIcon />&nbsp;Nuevo tipo
                        </Button>
                    </Grid>

                    <Divider />
                </Grid>

                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "start",
                        alignItems: "stretch",
                        mt: 1,
                    }}
                    spacing={2}
                >
                    {tiposMaquinas.map((tipoMaquina) => (
                        <Grid
                            key={tipoMaquina.codTipoMaquina}
                            container
                            direction="column"
                            size={{ xs: 12, md: 6, xl: 4 }}
                            className='card-main'
                            sx={{
                                p: 2,
                                gap: 1.5,
                                justifyContent: "space-between",
                            }}
                        >
                            <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "start", gap: 1 }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
                                        {tipoMaquina.desTipoMaquina}
                                    </Typography>

                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Tipo de maquina
                                    </Typography>
                                </Box>

                                <Chip
                                    size="small"
                                    label={tipoMaquina.codTipoMaquina}
                                    color="primary"
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ alignItems: "center", gap: 1 }}>
                                <SettingsApplicationsIcon color="primary" />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Codigo interno: {tipoMaquina.codTipoMaquina}
                                </Typography>
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "end", gap: 1 }}>
                                <Button variant="outlined" size="small" onClick={() => abrirEditarTipoMaquina(tipoMaquina.codTipoMaquina)}>
                                    <EditIcon />&nbsp;Editar
                                </Button>

                                <Button variant="outlined" color="error" size="small" onClick={() => setTipoMaquinaEliminar(tipoMaquina)}>
                                    <DeleteForeverIcon />&nbsp;Eliminar
                                </Button>
                            </Grid>
                        </Grid>
                    ))}

                    {tiposMaquinas.length === 0 && (
                        <Grid container direction="column" size={{ xs: 12 }} className='card-main' sx={{ p: 3, alignItems: "center" }}>
                            <PrecisionManufacturingIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="h6">
                                No hay tipos de maquinas
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Cuando el backend devuelva datos apareceran en este listado.
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "center", mt: 2 }} spacing={2}>
                    <Grid container direction="column" size={{ xs: 12, md: 8 }} sx={{ alignItems: "start" }}>
                        <Typography variant='h5'>
                            Colores de mis maquinas
                        </Typography>

                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                            Configura el color que veras en el fondo de cada linea del planificador.
                        </Typography>
                    </Grid>

                    <Grid container size={{ xs: 12, sm: 7, md: 4 }} className='card-main' sx={{ p: 1.5, alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormatColorFillIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                            <Typography variant="caption">Maquinas configurables</Typography>
                            <Typography variant="h5">{salidasProduccion.length}</Typography>
                        </Box>
                        </Box>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setMostrarColoresMaquinas((mostrar) => !mostrar)}
                        >
                            {mostrarColoresMaquinas ? <ExpandLessIcon /> : <ExpandMoreIcon />}&nbsp;
                            {mostrarColoresMaquinas ? 'Ocultar' : 'Ver maquinas'}
                        </Button>
                    </Grid>

                    <Divider />
                </Grid>

                {mostrarColoresMaquinas && (
                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "start",
                        alignItems: "stretch",
                    }}
                    spacing={2}
                >
                    {salidasProduccion.map((salida) => {
                        const colorMaquina = coloresPorMaquina[salida.codMaquina] || ''
                        const colorInputValue = isHexColor(colorMaquina) ? colorMaquina : '#ffffff'
                        const guardandoColor = String(guardandoColorMaquina ?? '') === String(salida.codMaquina)

                        return (
                            <Grid
                                key={salida.codMaquina}
                                container
                                direction="column"
                                size={{ xs: 12, md: 6, xl: 4 }}
                                className='card-main'
                                sx={{
                                    p: 2,
                                    gap: 1.5,
                                    justifyContent: "space-between",
                                    borderLeft: colorMaquina ? `4px solid ${colorMaquina}` : undefined,
                                }}
                            >
                                <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "start", gap: 1 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
                                            {salida.desMaquina}
                                        </Typography>

                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Codigo maquina: {salida.codMaquina}
                                        </Typography>
                                    </Box>

                                    <Chip
                                        size="small"
                                        label={salida.activa ? 'Activa' : 'Inactiva'}
                                        color={salida.activa ? 'success' : 'default'}
                                        variant={salida.activa ? 'filled' : 'outlined'}
                                    />
                                </Grid>

                                <Grid container direction="row" size={{ xs: 12 }} sx={{ gap: 1 }}>
                                    <Chip
                                        size="small"
                                        label={salida.tipos_maquinas?.desTipoMaquina || tiposPorCodigo[salida.tipoMaquina] || `Tipo ${salida.tipoMaquina}`}
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Chip
                                        size="small"
                                        label={colorMaquina || 'Sin color personal'}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid container direction="row" size={{ xs: 12 }} sx={{ alignItems: "center", gap: 1 }}>
                                    <Box
                                        component="input"
                                        type="color"
                                        value={colorInputValue}
                                        onChange={(event) => guardarColorUsuarioMaquina(salida.codMaquina, event.target.value)}
                                        disabled={guardandoColor}
                                        title="Seleccionar color"
                                        sx={{
                                            width: 54,
                                            height: 38,
                                            p: 0.35,
                                            borderRadius: '7px',
                                            border: '1px solid #CBD5E1',
                                            backgroundColor: '#FFFFFF',
                                            cursor: guardandoColor ? 'not-allowed' : 'pointer',
                                            boxSizing: 'border-box',
                                            '&::-webkit-color-swatch-wrapper': {
                                                p: 0,
                                            },
                                            '&::-webkit-color-swatch': {
                                                border: 'none',
                                                borderRadius: '5px',
                                            },
                                            '&::-moz-color-swatch': {
                                                border: 'none',
                                                borderRadius: '5px',
                                            },
                                        }}
                                    />

                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => eliminarColorUsuarioMaquina(salida.codMaquina)}
                                        disabled={guardandoColor || !colorMaquina}
                                    >
                                        <FormatColorResetIcon />&nbsp;Sin color
                                    </Button>
                                </Grid>
                            </Grid>
                        )
                    })}

                    {salidasProduccion.length === 0 && (
                        <Grid container direction="column" size={{ xs: 12 }} className='card-main' sx={{ p: 3, alignItems: "center" }}>
                            <PrecisionManufacturingIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="h6">
                                No hay maquinas configurables
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Cuando existan salidas de produccion apareceran aqui para asignarles tu color.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                )}
                </>
                )}
            </Grid>

            <Dialog open={openTipoMaquina} onClose={cerrarTipoMaquina} fullWidth maxWidth="sm">
                <form onSubmit={guardarTipoMaquina}>
                    <DialogTitle>
                        <Grid
                            container
                            direction="row"
                            size={{ xs: 12 }}
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="h5">
                                {tipoMaquinaEditandoCod ? 'Editar tipo de maquina' : 'Nuevo tipo de maquina'}
                            </Typography>

                            <Button variant="text" color="negro" onClick={cerrarTipoMaquina}>
                                <CloseIcon />
                            </Button>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        <Grid
                            container
                            direction="row"
                            size={{ xs: 12 }}
                            sx={{
                                justifyContent: "center",
                                alignItems: "center",
                                pt: 1,
                            }}
                            spacing={2}
                        >
                            <Grid container size={{ xs: 12 }}>
                                <TextField
                                    label="Descripcion"
                                    name="desTipoMaquina"
                                    value={tipoMaquinaForm.desTipoMaquina}
                                    onChange={handleChangeTipoMaquina}
                                    fullWidth
                                    required
                                />
                            </Grid>

                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button color="primary" onClick={cerrarTipoMaquina}>
                            Cancelar
                        </Button>

                        <Button color="primary" variant="contained" type="submit">
                            {tipoMaquinaEditandoCod ? 'Guardar tipo' : 'Crear tipo'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={Boolean(tipoMaquinaEliminar)} onClose={() => setTipoMaquinaEliminar(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    Eliminar tipo de maquina
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Desea eliminar el tipo de maquina {tipoMaquinaEliminar?.desTipoMaquina || ''}?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button color="primary" onClick={() => setTipoMaquinaEliminar(null)}>
                        Cancelar
                    </Button>

                    <Button color="error" variant="contained" onClick={confirmarEliminarTipoMaquina}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
